import { MilestoneSignal, BudgetHealthLabel } from "../ProjectDisplaySignals.jsx";
import { budgetHealth, milestoneSignal } from "../../../lib/projectDisplaySignals.js";
// Updates detail for one project in one week: ordered single page, no inner
// tabs, no meeting mode. Each section edits with an explicit local draft and
// save/cancel; editors appear only from server capabilities in the writable
// (current, unfrozen) week. Failed saves keep the draft open.

import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { T } from "../../../app/tokens.js";
import { useHoverIntent } from "../../../components/useHoverIntent.js";
import { useEditablePanel } from "../../../hooks/useEditablePanel.js";
import { FinancialAmountFields, financialDraft, financialPatch } from "../financials/FinancialAmountFields.jsx";
import { FinancialHistory } from "../financials/FinancialHistory.jsx";
import { ActualsThrough } from "../financials/ActualsThrough.jsx";
import { StatusPill } from "../../../v2/components/StatusPill.jsx";
import {
  patchWeeklyEntry, patchWeeklyFinancials, postLineCorrection, postTrackUpdate, putTrackStatus, postTrackResponse, TRACK_STATUSES, OVERALL_STATUSES,
} from "../../../data/projectWeeklyClient.js";
import { createDecision, updateDecision, createRisk, updateRisk } from "../../../data/projectsClient.js";
import { weeklyParticipation, PARTICIPATION_STATE_LABEL } from "../../../lib/weeklyParticipation.js";
import {
  OVERALL_STATUS_META, TRACK_STATUS_META, recordHealthMeta, linesForTrack, resolveLine, fmtIsoDate, fmtTimestamp, milestoneText, weeklyTrackState,
} from "./weeklyModel.js";
import { useProjectDocuments } from "../documents/useProjectDocuments.js";
import { UpdatesSidePanel } from "./UpdatesSidePanel.jsx";
import { useUpdatesPanelPreference } from "./useUpdatesPanelPreference.js";
import { SidePanelHost } from "../../../components/SidePanelRail.jsx";
import { btn, btnPrimary, inputStyle, muted, withDisabled } from "./weeklyUi.js";
import { WeeklyRichEditor, WeeklyRichText } from "./WeeklyRichEditor.jsx";
import { weeklyText } from "../../../lib/weeklyRichText.js";
import { assignedTrackCompleteness } from "../../../lib/weeklyTrackCompleteness.js";
import { readerSignals, sectionEmptiness, EMPTY_BAR_TEXT, EMPTY_SECTION_MESSAGE, hasFinancialValue } from "./readerSignals.js";
import { ProjectConnections } from "../connections/ProjectConnections.jsx";
import { LastPosted, CarryForward } from "./PriorSection.jsx";
import { weeklyTeam } from "../../../lib/weeklyTeam.js";
import { TeamContext, TeamCard, OwnerCircles, ContributorCircle } from "./WeeklyTeam.jsx";

const time = (v) => (Number.isFinite(Date.parse(v)) ? Date.parse(v) : 0);
const editBtn = { fontSize: T.fsXs, fontWeight: T.fwSemibold, color: T.blue, background: "transparent", border: 0, padding: "4px 0", cursor: "pointer", whiteSpace: "nowrap" };
const miniBtn = { ...btn, padding: "2px 9px", fontSize: T.fsXxs };
const label = { fontSize: T.fsXxs, fontWeight: T.fwRegular, letterSpacing: ".05em", textTransform: "uppercase", color: T.mid };
const ORG_COLORS = { jll: T.ragAmberText, ripple: T.blue };
const SEVERITY = { high: ["High", T.red, T.utilRed], med: ["Medium", T.ragAmberText, T.utilAmber], low: ["Low", T.green, T.greenSoft] };

function useAction(onMutate) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const mounted = useRef(true);
  const pending = useRef(false);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const run = async (fn) => {
    if (pending.current) return false;
    pending.current = true; setBusy(true); setError(null);
    try { await onMutate(fn); return mounted.current; }
    catch (err) { if (mounted.current) setError(err?.message || "The change could not be saved. Your draft is kept."); return false; }
    finally { pending.current = false; if (mounted.current) setBusy(false); }
  };
  return { busy, error, run, setError };
}

const asList = (v) => weeklyText(v).split("\n").map((x) => x.replace(/^\s*[-*]\s*/, "").trim()).filter(Boolean);
const asText = (v) => (Array.isArray(v) ? v.join("\n") : v || "");
const initials = (name) => (name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");
const orgKey = (org) => (/jll/i.test(org || "") ? "jll" : "ripple");
// Owner identity lives in the right-side circle; bylines keep only context.
function Byline({ extra }) {
  return extra ? <span data-testid="section-byline" style={{ fontSize: T.fsXs, color: T.mid, fontWeight: T.fwRegular }}>{extra}</span> : null;
}

function ErrorLine({ error, testId }) {
  return error ? <div role="alert" data-testid={testId} style={{ color: T.red, fontSize: T.fsXs, marginTop: 6 }}>{error}</div> : null;
}

function EditRow({ act, onSave, onCancel, disabled, testId }) {
  return (
    <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
      <button type="button" data-testid={`${testId}-save`} disabled={act.busy || disabled} onClick={onSave} style={withDisabled(btnPrimary, act.busy || disabled)}>{act.busy ? "Saving..." : "Save & post"}</button>
      <button type="button" data-testid={`${testId}-cancel`} disabled={act.busy} onClick={onCancel} style={btn}>Cancel</button>
    </div>
  );
}

const editBox = { background: T.card, border: `1px solid ${T.border}`, borderRadius: 11, padding: "12px 13px" };

const EDITING_SELECTOR = 'input:not([type="file"]):not([type="hidden"]), textarea, select, [contenteditable="true"]';
const barBtn = { display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left", background: "transparent", border: 0, padding: "10px 17px", cursor: "pointer", color: T.mid, fontSize: T.fsSm, fontWeight: T.fwSemibold };

// Full-width section card. With `emptyBar` (genuinely empty evidence only) it
// starts as a quiet one-line disclosure; expanding reveals the empty guidance
// and authorized actions. Collapsing is refused while an editor is open so an
// unsaved draft is never hidden.
function Ecard({ title, byline, owner = null, action, children, testId, emptyBar = null, emptyMessage = null, editing = false }) {
  const ref = useRef(null);
  const disclosureRef = useRef(null);
  const restoreFocus = useRef(false);
  const focusEditor = useRef(false);
  const wasEditing = useRef(editing);
  const bodyId = useId();
  const [open, setOpen] = useState(() => !emptyBar);
  const [blocked, setBlocked] = useState(false);
  const expanded = !emptyBar || open || editing;
  useLayoutEffect(() => {
    if (focusEditor.current) {
      focusEditor.current = false;
      ref.current?.querySelector(EDITING_SELECTOR)?.focus({ preventScroll: true });
    } else if (wasEditing.current && !editing && document.activeElement === document.body) {
      (disclosureRef.current || ref.current?.querySelector("button"))?.focus({ preventScroll: true });
    }
    wasEditing.current = editing;
  });
  useLayoutEffect(() => {
    if (!restoreFocus.current) return;
    restoreFocus.current = false;
    disclosureRef.current?.focus({ preventScroll: true });
  }, [open]);
  // A refresh can turn previously populated evidence into an empty section.
  // Never let that prop transition unmount an already-open editor.
  useEffect(() => {
    setOpen(!emptyBar || Boolean(ref.current?.querySelector(EDITING_SELECTOR)));
  }, [emptyBar]);
  if (emptyBar && !expanded) {
    return (
      <section data-testid={testId} data-collapsed="true" className="wk-ecard">
        <div style={{ display: "flex", alignItems: "center", paddingRight: emptyMessage ? 17 : 0 }}>
          <button ref={disclosureRef} type="button" data-testid={`${testId}-disclosure`} aria-expanded={false} aria-controls={bodyId} onClick={() => { restoreFocus.current = true; setBlocked(false); setOpen(true); }} style={barBtn}>
            <span aria-hidden="true" style={{ fontSize: T.fsXxs, color: T.light }}>▸</span>
            {emptyMessage ? <><span style={{ color: T.dark }}>{title}</span><span style={{ flex: 1, fontWeight: T.fwRegular, color: T.mid }}>{emptyMessage}</span></> : <span style={{ flex: 1 }}>{emptyBar}</span>}
            <span style={{ fontSize: T.fsXxs, color: T.light }}>Show</span>
          </button>
          <span className="wk-section-tools">
          {action && React.cloneElement(action, { onClick: (event) => {
            setBlocked(false); setOpen(true); focusEditor.current = true;
            action.props.onClick?.(event);
          } })}
          {owner && <span style={{ paddingRight: 8 }}>{owner}</span>}
          </span>
        </div>
        <div id={bodyId} hidden />
      </section>
    );
  }
  const collapse = () => {
    if (editing || ref.current?.querySelector(EDITING_SELECTOR)) { setBlocked(true); return; }
    restoreFocus.current = true; setBlocked(false); setOpen(false);
  };
  return (
    <section ref={ref} data-testid={testId} className="wk-ecard">
      <header className="wk-ecard-hd">
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", minWidth: 0 }}>
          <h3 style={{ margin: 0, fontSize: T.fsBase, fontWeight: T.fwRegular, color: T.dark }}>{title}</h3>{byline}
        </div>
        <span className="wk-section-tools">
          {emptyBar && <button ref={disclosureRef} type="button" data-testid={`${testId}-disclosure`} aria-expanded aria-controls={bodyId} onClick={collapse} style={{ ...miniBtn, color: T.mid }}>Hide</button>}
          {action}
          {owner}
        </span>
      </header>
      <div id={bodyId} style={{ padding: "14px 17px" }}>
        {blocked && <div role="status" data-testid={`${testId}-collapse-blocked`} style={{ ...muted, marginBottom: 8 }}>Save or cancel the open editor before hiding this section.</div>}
        {children}
      </div>
    </section>
  );
}

// PROJECTS-CALM-PASS R2: stats are ink/muted; only over budget is coloured.
const TONE = { warn: T.ragAmberText, danger: T.red, neutral: T.dark, unknown: T.light };
// Read-only evidence strip: plain list items, not buttons.
export function SignalStrip({ project }) {
  const signals = readerSignals(project);
  return (
    <ul data-testid="signal-strip" aria-label="Weekly signals" className="wk-stats">
      {signals.map((sig) => (
        <li key={sig.key} data-testid={`signal-${sig.key}`} data-tone={sig.tone} className="wk-stat">
          <span style={{ display: "block", fontSize: T.fsXxs, color: T.mid, fontWeight: T.fwRegular }}>{sig.label}</span>
          <span data-testid={`signal-${sig.key}-value`} style={{ display: "block", marginTop: 2, fontSize: T.fsBase, fontWeight: sig.key === "budget" || sig.tone === "unknown" ? T.fwRegular : T.fwSemibold, color: TONE[sig.tone] || T.dark, fontStyle: sig.tone === "unknown" ? "italic" : "normal", fontVariantNumeric: "tabular-nums" }}>{sig.value}</span>
          {sig.detail && <span style={{ display: "block", fontSize: T.fsXxs, color: T.mid }}>{sig.detail}</span>}
        </li>
      ))}
    </ul>
  );
}

const Empty = ({ children, testId }) => <div data-testid={testId} style={{ color: T.light, fontStyle: "italic", fontSize: T.fsSm }}>{children}</div>;

// ── Participation strip ────────────────────────────────────────────────────
export function Avatar({ slot, person }) {
  const intent = useHoverIntent();
  const st = slot.state;
  const org = person ? orgKey(person.org) : null;
  const tip = person
    ? `${person.displayName} · ${slot.label}${person.roleOnProject ? ` (${person.roleOnProject})` : ""} - ${PARTICIPATION_STATE_LABEL[st]}`
    : `${slot.label} - ${PARTICIPATION_STATE_LABEL[st]}`;
  const reset = intent.reset;
  useEffect(() => { reset(); }, [tip, reset]);
  const ring = st === "reported" ? `0 0 0 2px ${T.card}, 0 0 0 4px ${T.blue}` : "none";
  const style = !person
    ? { background: "transparent", color: T.light, border: `1.5px dashed ${T.light}` }
    : st === "not_yet" ? { background: T.card, color: T.light, border: `1.5px dashed ${T.light}` }
    : { background: ORG_COLORS[org], color: T.white, border: "2px solid transparent", boxShadow: ring, opacity: st === "quiet" ? 0.5 : 1 };
  return (
    <span {...intent.triggerProps} className="wk-av" tabIndex={0} aria-label={tip} data-testid={`participation-${slot.key}`} data-state={st}>
      <span style={{ width: 30, height: 30, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: T.fsXxs, fontWeight: T.fwDisplay, ...style }}>
        {person ? initials(person.displayName) : "--"}
      </span>
      {intent.open && <span className="wk-avtip" role="tooltip">{tip}</span>}
    </span>
  );
}

// Development stub for AI drafting: no network, no generated prose or facts.
// Opens the normal editor with the saved text preserved; the disclosure below
// is UI-only and never part of the submitted draft.
export const AI_STUB_NOTICE = "Development stub - no AI was run and nothing was generated. Your saved text is kept as the draft; write or revise it yourself, then Save to post. Cancel leaves the posted text unchanged.";
export function AiStubNotice({ testId }) {
  return <div role="note" data-testid={testId} style={{ fontSize: T.fsXs, color: T.mid, background: T.card, border: `1px dashed ${T.border}`, borderRadius: 8, padding: "6px 10px", marginBottom: 8 }}>{AI_STUB_NOTICE}</div>;
}
export const aiBtn = { fontSize: T.fsXs, fontWeight: T.fwRegular, color: T.blue, background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: "4px 11px", cursor: "pointer", textTransform: "none", letterSpacing: 0, whiteSpace: "nowrap" };

export function ParticipationStrip({ part }) {
  if (!part.available) {
    return <div data-testid="participation-unavailable" style={{ ...muted, fontStyle: "italic", padding: "12px 0 14px" }}>Ownership and track assignments were not captured with this archive - participation is unavailable.</div>;
  }
  const group = (title, slots) => (
    <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
      <span style={{ ...label, color: T.light }}>{title}</span>
      {slots.map((s) => (s.people.length ? s.people.slice(0, 1) : [null]).map((p) => <Avatar key={`${s.key}-${p?.personId || "none"}`} slot={s} person={p} />))}
    </div>
  );
  return (
    <div data-testid="participation-strip" style={{ display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap", padding: "12px 0 14px" }}>
      {group("Ownership", part.slots.filter((s) => s.group === "ownership"))}
      <span aria-hidden="true" style={{ width: 1, height: 28, background: T.border }} />
      {group("Tracks", part.slots.filter((s) => s.group === "tracks"))}
      <span aria-hidden="true" style={{ width: 1, height: 28, background: T.border }} />
      <span data-testid="participation-count" style={{ fontSize: T.fsXs, fontWeight: T.fwBold, color: T.mid }}><b style={{ color: T.blue }}>{part.responded}</b> of {part.total} in this week</span>
    </div>
  );
}

// ── Sections ───────────────────────────────────────────────────────────────
function healthMeta(project) {
  return OVERALL_STATUS_META[project.summary?.statusOverall] || null;
}

export function overallStripStyle(status) {
  const color = { on_track: T.greenDeep, at_risk: T.ragAmberText, critical: T.red }[status];
  return {
    color: color || T.mid,
    background: color ? `color-mix(in srgb, ${color} 10%, ${T.card})` : T.card,
    borderBottom: `1px solid ${color ? `color-mix(in srgb, ${color} 25%, ${T.card})` : T.border}`,
  };
}

function OverallHero({ project, can, live, weekDate, onMutate, compactEmpty = false }) {
  const s = project.summary || {};
  const meta = healthMeta(project);
  const strip = overallStripStyle(s.statusOverall);
  const panel = useEditablePanel(() => ({ statusOverall: s.statusOverall || "on_track", narrative: s.narrative || "" }));
  const act = useAction(onMutate);
  const { draft, setField, editing } = panel;
  const [stub, setStub] = useState(false);
  const [carried, setCarried] = useState(false);
  useEffect(() => { if (!editing) setCarried(false); }, [editing]);
  const [expanded, setExpanded] = useState(false);
  // Pinned only while reading a compact summary: an open editor or an
  // expanded long narrative scrolls normally so it never covers the viewport.
  const pinned = !editing && !expanded;
  const long = weeklyText(s.narrative).length > 280;
  const save = async () => { if (await act.run(() => patchWeeklyEntry(project.id, { weekDate, statusOverall: draft.statusOverall, narrative: draft.narrative }))) { panel.setEditing(false); setStub(false); } };
  return (
    <section data-testid="section-overall" data-pinned={pinned || undefined} className="wk-hero" style={{ background: T.card, borderColor: T.border, position: pinned ? "sticky" : "relative", top: pinned ? "var(--wk-overall-top, calc(var(--updates-sticky-top, 0px) + var(--wk-head-h, 0px) + 12px))" : undefined, zIndex: pinned ? 2 : undefined }}>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 220px", minWidth: 0 }}>
          <div data-testid="overall-title-strip" style={{ ...label, ...strip, padding: "12px 17px", textTransform: "none", letterSpacing: 0, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={{ color: T.dark, fontSize: T.fsBase }}>Overall status</span> <span data-testid="overall-weekly-status" title="Weekly status reported by the Delivery Owner; separate from the PM-owned project Status" style={{ textTransform: "none", letterSpacing: 0, fontWeight: T.fwRegular, color: strip.color }}>Weekly status: {meta?.label || "not set"}</span> <Byline extra={s.updatedAt ? fmtTimestamp(s.updatedAt) : null} />
            <span style={{ marginLeft: "auto", display: "inline-flex", gap: 8, alignItems: "center", textTransform: "none", letterSpacing: 0 }}>
            {can && !editing && <span className="wk-section-tools">
              <button type="button" data-testid="ai-draft-overall" onClick={() => { act.setError(null); setStub(true); panel.setEditing(true); }} style={aiBtn}>Draft with AI</button>
              <button type="button" data-testid="edit-overall" onClick={() => { act.setError(null); setStub(false); panel.setEditing(true); }} style={{ ...editBtn, textTransform: "none", letterSpacing: 0, whiteSpace: "nowrap" }}>Edit</button>
            </span>}
            <OwnerCircles roleKey="delivery_owner" testId="owner-overall" />
            </span>
          </div>
          <div style={{ padding: "14px 17px" }}>
          {can && editing ? (
            <div style={{ ...editBox, marginTop: 8 }}>
              {stub && <AiStubNotice testId="overall-ai-stub" />}
              <CarryForward source={project.priorSections?.summary?.narrative} value={draft.narrative} savedValue={s.narrative} carried={carried} disabled={act.busy} testId="overall-carry" onCarry={source => { setField("narrative", source.text); setCarried(true); setStub(false); }} />
              <select aria-label="Overall weekly status" data-testid="overall-status-input" disabled={act.busy} value={draft.statusOverall} onChange={(e) => setField("statusOverall", e.target.value)} style={{ ...inputStyle, width: "auto", marginBottom: 8 }}>
                {OVERALL_STATUSES.map((v) => <option key={v} value={v}>{OVERALL_STATUS_META[v].label}</option>)}
              </select>
              <WeeklyRichEditor aria-label="Overall status narrative" data-testid="overall-narrative-input" disabled={act.busy} value={draft.narrative} onChange={value => setField("narrative", value)} />
              <EditRow act={act} testId="overall" disabled={stub && !draft.narrative.trim()} onSave={save} onCancel={() => { panel.reset(); setStub(false); act.setError(null); }} />
            </div>
          ) : s.narrative ? (
            <>
              <WeeklyRichText value={s.narrative} data-testid="overall-text" style={{ margin: "7px 0 0", fontSize: T.fsBase, lineHeight: 1.55, color: T.dark, whiteSpace: "pre-wrap", ...(long && !expanded ? { display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" } : null) }} />
              {long && <button type="button" data-testid="overall-more" aria-expanded={expanded} onClick={() => setExpanded((v) => !v)} style={{ ...miniBtn, marginTop: 6 }}>{expanded ? "Show less" : "Read full status"}</button>}
            </>
          ) : !compactEmpty && <div style={{ marginTop: 7 }}><Empty testId="overall-empty">Overall status not yet posted by the Delivery Owner.</Empty></div>}
          <ErrorLine error={act.error} testId="overall-error" />
          {live && !weeklyText(s.narrative).trim() && <LastPosted source={project.priorSections?.summary?.narrative} testId="overall-last-posted" />}
          </div>
        </div>
      </div>
    </section>
  );
}

function DecisionEditor({ project, decision, weekDate, onMutate, onDone }) {
  const panel = useEditablePanel(() => ({ title: decision?.title || "", neededByDate: (decision?.neededByDate || "").slice(0, 10) }));
  const act = useAction(onMutate);
  const { draft, setField } = panel;
  const { setEditing } = panel;
  useEffect(() => { setEditing(true); }, [setEditing]);
  const close = () => { panel.reset(); act.setError(null); onDone(); };
  const save = async () => {
    const payload = { title: draft.title.trim(), neededByDate: draft.neededByDate || null, weekDate };
    if (await act.run(() => (decision ? updateDecision(project.id, decision.id, payload) : createDecision(project.id, payload)))) { panel.setEditing(false); onDone(); }
  };
  return (
    <div style={{ ...editBox, marginTop: 10 }}>
      <label style={label}>Decision<input aria-label="Decision title" data-testid="decision-title-input" disabled={act.busy} value={draft.title || ""} onChange={(e) => setField("title", e.target.value)} style={{ ...inputStyle, marginTop: 4 }} /></label>
      <label style={{ ...label, display: "block", marginTop: 8 }}>Needed by<input type="date" aria-label="Needed by" disabled={act.busy} value={draft.neededByDate || ""} onChange={(e) => setField("neededByDate", e.target.value)} style={{ ...inputStyle, width: "auto", marginTop: 4, display: "block" }} /></label>
      <EditRow act={act} testId="decision" disabled={!(draft.title || "").trim()} onSave={save} onCancel={close} />
      <ErrorLine error={act.error} testId="decision-error" />
    </div>
  );
}

// R1: one red phrase, date stays muted. Unknown time zone stays unknown.
function DecisionOverdue({ date, timeZone, testId }) {
  if (!date) return null;
  const sig = milestoneSignal({ dueDate: date }, timeZone);
  if (!sig.overdue) return null;
  return <> · <span data-testid={testId} style={{ color: T.red, whiteSpace: "nowrap" }}>{sig.days}d overdue</span></>;
}

function DecisionsSection({ project, can, onMutate, weekDate, emptyBar }) {
  const available = Array.isArray(project.decisions);
  const open = available ? project.decisions.filter((d) => !d.status || d.status === "open") : [];
  // Keep the opening record and editor mounted even if a refresh removes the
  // item from the open list. The existing save path still validates on server.
  const [target, setTarget] = useState(null); // "new" | opening decision snapshot
  return (
    <Ecard testId="section-decisions" emptyBar={emptyBar} emptyMessage={EMPTY_SECTION_MESSAGE.decisions} editing={Boolean(target)} title="Decisions" owner={<OwnerCircles roleKey="market_pm" testId="owner-decisions" />}
      action={can && !target && <button type="button" data-testid="decision-add" aria-label="Edit decisions" style={editBtn} onClick={() => setTarget("new")}>Edit</button>}>
      {!available ? <Empty>Decisions were not captured for this week.</Empty> : open.length === 0 && !target && <Empty>No open decisions.</Empty>}
      {open.map((d) => (
        <div key={d.id} data-testid={`decision-${d.id}`} style={{ padding: "10px 0", borderBottom: `1px solid ${T.lightGray}` }}>
          <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: T.fsSm, fontWeight: T.fwRegular, color: T.dark }}>{d.title}</div>
              <div style={{ ...muted, marginTop: 2 }}>{d.owner ? `${d.owner} · ` : ""}{d.neededByDate ? `needed by ${fmtIsoDate(d.neededByDate)}` : "no needed-by date"}<DecisionOverdue date={d.neededByDate} timeZone={project.projectTimeZone} testId={`decision-overdue-${d.id}`} /></div>
            </div>
            <ContributorCircle item={d} roleKey="market_pm" testId={`decision-contributor-${d.id}`} />
            {can && !target && <button type="button" className="wk-action" data-testid={`decision-edit-${d.id}`} style={miniBtn} onClick={() => setTarget(d)}>Edit</button>}
          </div>
        </div>
      ))}
      {can && target && <DecisionEditor project={project} decision={target === "new" ? null : target} weekDate={weekDate} onMutate={onMutate} onDone={() => setTarget(null)} />}
    </Ecard>
  );
}

function List({ items, testId }) {
  return items.length ? <ul data-testid={testId} style={{ margin: 0, paddingLeft: 18 }}>{items.map((x, i) => <li key={i} style={{ fontSize: T.fsSm, marginBottom: 4, color: T.dark }}><WeeklyRichText value={x} /></li>)}</ul> : <Empty testId={`${testId}-empty`}>Not yet posted.</Empty>;
}

export function PmoSection({ project, can, weekDate, onMutate, emptyBar, onPendingChange }) {
  const note = project.summary?.pmoNote || "";
  const panel = useEditablePanel(() => ({ pmoNote: note }));
  const act = useAction(onMutate);
  useEffect(() => { onPendingChange?.(panel.editing || act.busy); }, [panel.editing, act.busy, onPendingChange]);
  const save = async () => { if (await act.run(() => patchWeeklyEntry(project.id, { weekDate, pmoNote: panel.draft.pmoNote }))) panel.setEditing(false); };
  return (
    <Ecard testId="section-pmo" emptyBar={emptyBar} title="PMO note" owner={<OwnerCircles roleKey="pmo_pm" testId="owner-pmo" />}
      action={can && !panel.editing && <button type="button" data-testid="edit-pmo" onClick={() => { act.setError(null); panel.setEditing(true); }} style={editBtn}>Edit</button>}>
      {can && panel.editing ? (
        <div style={editBox}>
          <WeeklyRichEditor aria-label="PMO note" data-testid="pmo-input" disabled={act.busy} value={panel.draft.pmoNote} onChange={value => panel.setField("pmoNote", value)} />
          <EditRow act={act} testId="pmo" onSave={save} onCancel={() => { panel.reset(); act.setError(null); }} />
        </div>
      ) : note ? <WeeklyRichText value={note} data-testid="pmo-text" style={{ fontSize: T.fsSm, color: T.dark, whiteSpace: "pre-wrap" }} /> : <Empty>{Object.hasOwn(project, "summary") ? "PMO note not yet posted." : "PMO note evidence was not captured for this week."}</Empty>}
      <ErrorLine error={act.error} testId="pmo-error" />
    </Ecard>
  );
}

export function ThisWeekSection({ project, caps, weekDate, onMutate, emptyBar, onPendingChange }) {
  const s = project.summary || {};
  const panel = useEditablePanel(() => ({ accomplishments: asText(s.accomplishments), upcoming: asText(s.upcoming) }));
  const act = useAction(onMutate);
  const { draft, setField } = panel;
  const editing = caps.thisWeek && panel.editing;
  useEffect(() => { onPendingChange?.(panel.editing || act.busy); }, [panel.editing, act.busy, onPendingChange]);
  const [carried, setCarried] = useState({});
  useEffect(() => { if (!editing) setCarried({}); }, [editing]);
  const save = async () => {
    if (await act.run(() => patchWeeklyEntry(project.id, { weekDate, accomplishments: draft.accomplishments, upcoming: draft.upcoming }))) panel.setEditing(false);
  };
  const blockers = asList(s.blockers);
  return (
    <Ecard testId="section-this-week" emptyBar={emptyBar} emptyMessage={EMPTY_SECTION_MESSAGE.thisWeek} editing={editing} title="Achievements & upcoming" owner={<OwnerCircles roleKey="market_pm" testId="owner-this-week" />}
      action={caps.thisWeek && !editing && <button type="button" data-testid="edit-this-week" style={editBtn} onClick={() => { act.setError(null); panel.setEditing(true); }}>Edit</button>}>
      {editing ? (
        <div style={editBox}>
          <div className="wk-cols2">
            <div><div style={label}>Achievements</div>
              <CarryForward source={project.priorSections?.summary?.accomplishments} value={draft.accomplishments} savedValue={s.accomplishments} carried={carried.accomplishments} disabled={act.busy} testId="accomplishments-carry" onCarry={source => { setField("accomplishments", source.text); setCarried(c => ({ ...c, accomplishments: true })); }} />
              <WeeklyRichEditor aria-label="Achievements" data-testid="achievements-input" disabled={act.busy} value={draft.accomplishments} onChange={value => setField("accomplishments", value)} /></div>
            <div><div style={label}>Upcoming</div>
              <CarryForward source={project.priorSections?.summary?.upcoming} value={draft.upcoming} savedValue={s.upcoming} carried={carried.upcoming} disabled={act.busy} testId="upcoming-carry" onCarry={source => { setField("upcoming", source.text); setCarried(c => ({ ...c, upcoming: true })); }} />
              <WeeklyRichEditor aria-label="Upcoming" data-testid="upcoming-input" disabled={act.busy} value={draft.upcoming} onChange={value => setField("upcoming", value)} /></div>
          </div>
          <div style={{ ...muted, marginTop: 6 }}>One item per line.</div>
          <EditRow act={act} testId="this-week" onSave={save} onCancel={() => { panel.reset(); act.setError(null); }} />
        </div>
      ) : !Object.hasOwn(project, "summary") ? <Empty>This week's achievements and upcoming evidence were not captured.</Empty> : (
        <div className="wk-cols2">
          <div><div style={{ ...label, marginBottom: 8 }}>Achievements</div>{weeklyText(s.accomplishments) ? <WeeklyRichText value={s.accomplishments} data-testid="achievements" /> : <Empty>Not yet posted.</Empty>}</div>
          <div><div style={{ ...label, marginBottom: 8 }}>Upcoming</div>{weeklyText(s.upcoming) ? <WeeklyRichText value={s.upcoming} data-testid="upcoming" /> : <Empty>Not yet posted.</Empty>}</div>
        </div>
      )}
      <ErrorLine error={act.error} testId="this-week-error" />
      {blockers.length > 0 && <div data-testid="blockers" style={{ marginTop: 12 }}><div style={{ ...label, marginBottom: 6 }}>Blockers</div><List items={blockers} testId="blockers-list" /></div>}
    </Ecard>
  );
}

const FIN_FIELDS = [["budget", "Budget", "approvedBudget"], ["spendToDate", "Spend to date", "spentToDate"], ["forecast", "Forecast", "forecastAtCompletion"], ["variance", "Variance", null]];
function money(v, currency = "USD") {
  if (!hasFinancialValue(v)) return null;
  const n = Number(v);
  const abs = Math.abs(n);
  const body = abs >= 1e6 ? `${(abs / 1e6).toFixed(1)}M` : abs >= 1e3 ? `${(abs / 1e3).toFixed(0)}k` : abs.toFixed(0);
  return `${n < 0 ? "-" : ""}${currency === "USD" ? "$" : `${currency} `}${body}`;
}

// R2: favourable/zero variance stays ink; amber only <=5% over, red above.
function overColor(f) {
  const st = budgetHealth(f.budget, f.forecast)?.state;
  return st === "over" ? T.red : st === "amber" ? T.ragAmberText : T.dark;
}

function FinancialsSection({ project, can, onMutate, frozen, weekDate, emptyBar }) {
  const f = project.financials;
  const editable = FIN_FIELDS.filter(([, , field]) => field && (!Array.isArray(f?.editableFields) || f.editableFields.includes(field)));
  const panel = useEditablePanel(() => financialDraft(f));
  const [history, setHistory] = useState(false);
  const act = useAction(onMutate);
  const editing = can && f?.financialsTracked !== false && panel.editing;
  const save = async () => {
    let patch;
    try { patch = financialPatch(panel.draft, f); } catch (error) { act.setError(error.message); return; }
    if (!Object.keys(patch).length) { panel.reset(); return; }
    if (await act.run(() => patchWeeklyFinancials(project.id, { weekDate, ...patch }))) panel.setEditing(false);
  };
  return (
    <Ecard testId="section-financials" emptyBar={emptyBar} emptyMessage={EMPTY_SECTION_MESSAGE.financials} editing={editing} title="Financials" byline={<Byline extra={f?.currency ? `${f.currency}, ex tax` : null} />} owner={<OwnerCircles roleKey="market_pm" testId="owner-financials" />}
      action={can && f && f.financialsTracked !== false && editable.length > 0 && !editing && <button type="button" data-testid="edit-financials" style={editBtn} onClick={() => { act.setError(null); panel.setEditing(true); }}>Edit</button>}>
      {editing ? (
        <div style={editBox}>
          <FinancialAmountFields draft={panel.draft} financials={f} setField={panel.setField} busy={act.busy} />
          <EditRow act={act} testId="financials" onSave={save} onCancel={() => { panel.reset(); act.setError(null); }} />
        </div>
      ) : !f ? <Empty testId="financials-unavailable">{frozen ? "Financials were not captured with this archive." : "Financial fields are not available for this project."}</Empty> : (
        <div className="wk-fin">{FIN_FIELDS.map(([k, l]) => {
          const v = money(f[k], f.currency || "USD");
          return (
            <div key={k} data-testid={`fin-${k}`} style={{ padding: "4px 0" }}>
              <div style={{ fontSize: T.fsXxs, color: T.mid }}>{l}</div>
              <div data-testid={`fin-${k}-value`} style={{ whiteSpace: "nowrap", fontSize: v ? T.fsLg : T.fsSm, fontWeight: v ? T.fwSemibold : T.fwRegular, color: k === "variance" && v ? overColor(f) : v ? T.dark : T.light, marginTop: 2, fontVariantNumeric: "tabular-nums" }}>{k === "variance" && f[k] > 0 ? "+" : ""}{v?.replace(/^-/, "−") || "Not recorded"}{k === "variance" && <> <BudgetHealthLabel budget={f.budget} forecast={f.forecast} currency={f.currency || "USD"} /></>}</div>
              {k === "spendToDate" && v && <ActualsThrough date={f.actualsThrough} includesPlan={f.includesPlan} />}
            </div>
          );
        })}</div>
      )}
      <ErrorLine error={act.error} testId="financials-error" />
      {f && !frozen && <><button type="button" style={{ ...editBtn, marginTop: 8 }} onClick={() => setHistory(value => !value)} aria-expanded={history}>History</button>
        {history && <FinancialHistory projectId={project.id} revision={`${f.spendToDate}-${f.budget}-${f.forecast}`} />}</>}
    </Ecard>
  );
}

function RiskEditor({ project, risk, weekDate, onMutate, onDone }) {
  const panel = useEditablePanel(() => ({ title: risk?.title || "", severity: risk?.severity || "med", mitigation: risk?.mitigation || "" }));
  const act = useAction(onMutate);
  const { draft, setField, setEditing } = panel;
  useEffect(() => { setEditing(true); }, [setEditing]);
  const close = () => { panel.reset(); act.setError(null); onDone(); };
  const save = async () => {
    const payload = { title: draft.title.trim(), severity: draft.severity, mitigation: draft.mitigation, weekDate };
    if (await act.run(() => (risk ? updateRisk(project.id, risk.id, payload) : createRisk(project.id, payload)))) { panel.setEditing(false); onDone(); }
  };
  return (
    <div style={{ ...editBox, marginTop: 10 }}>
      <label style={label}>Risk<input aria-label="Risk title" data-testid="risk-title-input" disabled={act.busy} value={draft.title || ""} onChange={(e) => setField("title", e.target.value)} style={{ ...inputStyle, marginTop: 4 }} /></label>
      <label style={{ ...label, display: "block", marginTop: 8 }}>Severity<select aria-label="Risk severity" disabled={act.busy} value={draft.severity || "med"} onChange={(e) => setField("severity", e.target.value)} style={{ ...inputStyle, width: "auto", display: "block", marginTop: 4 }}>
        {Object.entries(SEVERITY).map(([k, v]) => <option key={k} value={k}>{v[0]}</option>)}
      </select></label>
      <label style={{ ...label, display: "block", marginTop: 8 }}>Mitigation<textarea aria-label="Risk mitigation" rows={2} disabled={act.busy} value={draft.mitigation || ""} onChange={(e) => setField("mitigation", e.target.value)} style={{ ...inputStyle, marginTop: 4 }} /></label>
      <EditRow act={act} testId="risk" disabled={!(draft.title || "").trim()} onSave={save} onCancel={close} />
      <ErrorLine error={act.error} testId="risk-error" />
    </div>
  );
}

function RisksSection({ project, can, onMutate, frozen, weekDate, emptyBar }) {
  const risks = Array.isArray(project.risks) ? project.risks.filter((r) => !r.status || r.status === "open") : null;
  // Like Decisions, the draft must outlive refreshed open-item membership.
  const [target, setTarget] = useState(null);
  return (
    <Ecard testId="section-risks" emptyBar={emptyBar} emptyMessage={EMPTY_SECTION_MESSAGE.risks} editing={Boolean(target)} title="Risks" owner={<OwnerCircles roleKey="market_pm" testId="owner-risks" />}
      action={can && risks && !target && <button type="button" data-testid="risk-add" aria-label="Edit risks" style={editBtn} onClick={() => setTarget("new")}>Edit</button>}>
      {risks === null ? <Empty>{frozen ? "Risks were not captured with this archive." : "Risks are unavailable."}</Empty> : risks.length === 0 && !target ? <Empty>No open risks.</Empty> : null}
      {(risks || []).map((r) => {
        const sev = SEVERITY[r.severity];
        return (
          <div key={r.id} data-testid={`risk-${r.id}`} style={{ padding: "10px 0", borderBottom: `1px solid ${T.lightGray}` }}>
            <div style={{ display: "flex", gap: 11, alignItems: "flex-start" }}>
              {sev ? <StatusPill size="sm" label={sev[0]} testId={`risk-severity-${r.id}`} aria-label={`Severity ${sev[0]}`} /> : <StatusPill size="sm" label="Unrated" />}
              <div style={{ flex: 1, minWidth: 0, fontSize: T.fsSm, color: T.dark }}>
                <div>{r.title}</div>
                {r.mitigation && <div style={{ ...muted, marginTop: 2 }}>Mitigation: {r.mitigation}</div>}
              </div>
              <ContributorCircle item={r} roleKey="market_pm" testId={`risk-contributor-${r.id}`} />
              {can && !target && <button type="button" className="wk-action" data-testid={`risk-edit-${r.id}`} style={miniBtn} onClick={() => setTarget(r)}>Edit</button>}
            </div>
          </div>
        );
      })}
      {can && target && <RiskEditor project={project} risk={target === "new" ? null : target} weekDate={weekDate} onMutate={onMutate} onDone={() => setTarget(null)} />}
    </Ecard>
  );
}

// Correction of one line: explicit local draft, saved against the latest revision.
function LineCorrection({ project, trackKey, line, weekDate, onMutate }) {
  const r = resolveLine(line, { rich: true });
  const panel = useEditablePanel(() => ({ action: "replace", replacement: r.current || r.original || "" }));
  const act = useAction(onMutate);
  const { draft, setField } = panel;
  const revision = Number.isInteger(line.revision) ? line.revision : 0;
  const save = async () => {
    const ok = await act.run(() => postLineCorrection(project.id, trackKey, line.id, {
      weekDate, revision, action: draft.action, replacement: draft.action === "replace" ? draft.replacement : undefined,
    }));
    if (ok) panel.setEditing(false);
  };
  if (!panel.editing) {
    return <button type="button" data-testid={`line-correct-${line.id}`} style={{ ...miniBtn, marginLeft: 6 }} onClick={() => { act.setError(null); panel.setEditing(true); }}>Correct</button>;
  }
  return (
    <div style={{ ...editBox, marginTop: 6 }}>
      <div role="radiogroup" aria-label="Correction" style={{ display: "flex", gap: 12, fontSize: T.fsXs, marginBottom: 6 }}>
        <label><input type="radio" name={`corr-${line.id}`} data-testid={`line-action-replace-${line.id}`} checked={draft.action === "replace"} disabled={act.busy} onChange={() => setField("action", "replace")} /> Replace text</label>
        {!r.struck && <label><input type="radio" name={`corr-${line.id}`} data-testid={`line-action-strike-${line.id}`} checked={draft.action === "strike"} disabled={act.busy} onChange={() => setField("action", "strike")} /> Strike</label>}
      </div>
      {draft.action === "replace" && <WeeklyRichEditor aria-label="Replacement text" data-testid={`line-replacement-${line.id}`} disabled={act.busy} value={draft.replacement} onChange={value => setField("replacement", value)} />}
      <EditRow act={act} testId={`line-${line.id}`} disabled={draft.action === "replace" && !draft.replacement.trim()} onSave={save} onCancel={() => { panel.reset(); act.setError(null); }} />
      <ErrorLine error={act.error} testId={`line-error-${line.id}`} />
    </div>
  );
}

function LineHistory({ project, trackKey, line, canCorrect, weekDate, onMutate }) {
  const r = resolveLine(line, { rich: true });
  return (
    <li data-testid={`line-${line.id}`} style={{ margin: "6px 0", fontSize: T.fsSm, color: T.dark, lineHeight: 1.5 }}>
      {r.corrected ? <><s style={{ color: T.mid }}>{weeklyText(r.original)}</s>{r.current && <WeeklyRichText data-testid={`line-current-${line.id}`} value={r.current} />}{r.struck && <span style={{ ...muted, marginLeft: 6 }}>(struck)</span>}</> : <WeeklyRichText data-testid={`line-current-${line.id}`} value={r.original} />}
      <span style={{ ...muted, marginLeft: 6 }}>{line.authorName || "Unattributed"}{line.authorOrg ? `, ${line.authorOrg}` : ""}{line.createdAt ? ` · ${fmtTimestamp(line.createdAt)}` : ""}</span>
      <span style={{ display: "inline-flex", verticalAlign: "middle", marginLeft: 6 }}><ContributorCircle item={line} roleKey={trackKey} testId={`line-contributor-${line.id}`} /></span>
      {canCorrect && <LineCorrection project={project} trackKey={trackKey} line={line} weekDate={weekDate} onMutate={onMutate} />}
      {r.history.length > 0 && (
        <ul data-testid={`line-history-${line.id}`} style={{ margin: "2px 0 0 14px", padding: 0, listStyle: "none" }}>
          {r.history.map((c) => (
            <li key={c.id} style={{ ...muted, fontSize: T.fsXxs }}>{c.action === "strike" ? "Struck" : `Replaced with "${weeklyText(c.replacement)}"`} by {c.correctorName || "unknown"}{c.createdAt ? ` · ${fmtTimestamp(c.createdAt)}` : ""}</li>
          ))}
        </ul>
      )}
    </li>
  );
}

function TrackRow({ project, slot, caps, live, weekDate, onMutate }) {
  const key = slot.key;
  const st = weeklyTrackState(project, key);
  const lines = [...linesForTrack(project, key)].sort((a, b) => time(a.createdAt) - time(b.createdAt));
  const effective = [...lines].reverse().find((l) => !resolveLine(l).struck) || null;
  const canPost = live && (caps.canPostTracks || []).includes(key);
  const canStatus = live && (caps.canSetTrackStatus || []).includes(key);
  const canQuiet = live && (caps.canQuietTracks || []).includes(key);
  const canCorrect = live && (caps.canCorrectTracks || []).includes(key);
  const canEdit = canPost || canStatus || canQuiet;
  const panel = useEditablePanel(() => ({ note: "", status: st.status || "", quiet: st.state === "quiet" }));
  const act = useAction(onMutate);
  const [showHistory, setShowHistory] = useState(false);
  const { draft, setField } = panel;
  const editing = canEdit && panel.editing;
  const [carried, setCarried] = useState(false);
  useEffect(() => { if (!editing) setCarried(false); }, [editing]);
  const prior = live ? project.priorSections?.tracks?.[key] : null;
  const statusChanged = canStatus && draft.status && draft.status !== (st.status || "");
  const quietChanged = canQuiet && draft.quiet !== (st.state === "quiet");
  const noteReady = canPost && (draft.note || "").trim();
  const dirty = Boolean(statusChanged || quietChanged || noteReady);
  // Sequential, explicit save. Each acknowledged step is cleared from the draft
  // immediately so a retry after a later failure never re-appends the note.
  // A new note/status returns the track to reporting server-side, so a quiet
  // draft is re-posted explicitly after them.
  const save = async () => {
    const sendQuiet = quietChanged || (canQuiet && draft.quiet && Boolean(noteReady || statusChanged));
    const ok = await act.run(async () => {
      if (noteReady) { await postTrackUpdate(project.id, key, { weekDate, note: draft.note }); setField("note", ""); }
      if (statusChanged) await putTrackStatus(project.id, key, { weekDate, status: draft.status });
      if (sendQuiet) await postTrackResponse(project.id, key, { weekDate, response: draft.quiet ? "quiet" : "reporting" });
    });
    if (ok) panel.setEditing(false);
  };
  const state = slot.state === "unavailable" ? st.state : slot.state;
  const dot = state === "reported" ? { background: T.mid } : state === "quiet" ? { background: T.light } : { background: T.card, border: `1.5px dashed ${T.light}` };
  const health = st.status ? TRACK_STATUS_META[st.status] : null;
  let body;
  if (st.state === "reported" && effective) body = <WeeklyRichText value={resolveLine(effective, { rich: true }).current} />;
  else if (st.state === "reported") body = <span style={{ color: T.mid }}>{lines.length ? "No effective update - all lines struck" : "Status set - no update text"}</span>;
  else if (st.state === "quiet") body = <span style={{ color: T.mid, fontStyle: "italic" }}>Nothing this week</span>;
  else body = <span style={{ color: T.light, fontStyle: "italic" }}>Not yet reported</span>;
  const hasHistory = lines.length > 0 || Boolean(st.response) || Boolean(st.status);
  return (
    <div data-testid={`track-${key}`} data-state={state} style={{ padding: "11px 0", borderBottom: `1px solid ${T.lightGray}` }}>
      <div className="wk-trackrow">
        <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: "50%", flexShrink: 0, boxSizing: "border-box", ...dot }} />
        <div style={{ minWidth: 160, flexShrink: 0 }}>
          <div style={{ fontSize: T.fsSm, fontWeight: T.fwRegular, color: T.dark }}>{slot.label}</div>
        </div>
        <div style={{ flex: 1, minWidth: 0, fontSize: T.fsSm, color: T.dark }}>{body}</div>
        {effective && <ContributorCircle item={effective} roleKey={key} testId={`track-contributor-${key}`} />}
        {health && <StatusPill size="sm" label={health.label} fg={T.mid} bg={T.card} testId={`track-health-${key}`} />}
        {canEdit && !editing && <button type="button" className="wk-section-edit" data-testid={`track-edit-${key}`} style={editBtn} onClick={() => { act.setError(null); panel.setEditing(true); }}>Edit</button>}
        <OwnerCircles roleKey={key} testId={`track-owner-${key}`} />
        {hasHistory && <button type="button" data-testid={`track-history-${key}`} aria-expanded={showHistory} onClick={() => setShowHistory((v) => !v)} style={{ border: 0, background: "transparent", color: T.blue, fontSize: T.fsXxs, fontWeight: T.fwSemibold, cursor: "pointer" }}>{showHistory ? "Hide" : lines.length > 1 ? `Details (${lines.length})` : "Details"}</button>}
      </div>
      {!effective && <LastPosted source={prior} testId={`track-last-posted-${key}`} />}
      {editing && (
        <div style={{ ...editBox, margin: "8px 0 0 23px" }}>
          {canPost && <CarryForward source={prior} value={draft.note} savedValue={effective?.note} carried={carried} disabled={act.busy} testId={`track-carry-${key}`} onCarry={source => { setField("note", source.text); setCarried(true); }} />}
          {canPost && <div><div style={label}>New update</div><WeeklyRichEditor aria-label={`${slot.label} update`} data-testid={`track-input-${key}`} disabled={act.busy} value={draft.note} onChange={value => setField("note", value)} /></div>}
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center", marginTop: 8 }}>
            {canStatus && (
              <label style={{ ...label, display: "inline-flex", alignItems: "center", gap: 6 }}>Health
                <select aria-label={`Set ${slot.label} status`} data-testid={`track-status-${key}`} value={draft.status} disabled={act.busy} onChange={(e) => setField("status", e.target.value)} style={{ ...inputStyle, width: "auto", padding: "1px 6px", fontSize: T.fsXxs }}>
                  <option value="" disabled>Set status</option>
                  {TRACK_STATUSES.map((s) => <option key={s} value={s}>{TRACK_STATUS_META[s].label}</option>)}
                </select>
              </label>
            )}
            {canQuiet && <label style={{ fontSize: T.fsXs, color: T.dark, display: "inline-flex", alignItems: "center", gap: 6 }}><input type="checkbox" data-testid={`track-quiet-${key}`} checked={draft.quiet} disabled={act.busy} onChange={(e) => setField("quiet", e.target.checked)} />Nothing this week</label>}
          </div>
          <EditRow act={act} testId={`track-${key}`} disabled={!dirty} onSave={save} onCancel={() => { panel.reset(); act.setError(null); }} />
        </div>
      )}
      <ErrorLine error={act.error} testId={`track-error-${key}`} />
      {showHistory && (
        <div data-testid={`track-details-${key}`} style={{ margin: "8px 0 0 23px", padding: "6px 10px", background: T.lightGray, borderRadius: 8 }}>
          {st.status && <div style={muted}>Health {TRACK_STATUS_META[st.status]?.label || st.status}{(project.statuses || []).find((x) => x.trackKey === key)?.setterName ? ` - set by ${(project.statuses || []).find((x) => x.trackKey === key).setterName}` : ""}</div>}
          {st.response && <div style={muted}>{st.response.response === "quiet" ? "Marked nothing this week" : "Returned to reporting"} by {st.response.authorName || "unknown"}{st.response.createdAt ? ` · ${fmtTimestamp(st.response.createdAt)}` : ""}</div>}
          {lines.length > 0 && <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>{lines.map((l) => <LineHistory key={l.id} project={project} trackKey={key} line={l} canCorrect={canCorrect} weekDate={weekDate} onMutate={onMutate} />)}</ul>}
        </div>
      )}
    </div>
  );
}

function TracksSection({ project, part, caps, live, weekDate, onMutate }) {
  const slots = part.slots.filter((s) => s.group === "tracks");
  return (
    <Ecard testId="section-tracks" title="Tracks" byline={<span style={{ fontSize: T.fsXs, color: T.mid, fontWeight: T.fwSemibold }}>each track owner posts their own row</span>}>
      {!part.available && <div style={{ ...muted, fontStyle: "italic", marginBottom: 6 }}>Track assignments were not captured for this archive.</div>}
      {slots.map((s) => <TrackRow key={s.key} project={project} slot={s} caps={caps} live={live} weekDate={weekDate} onMutate={onMutate} />)}
    </Ecard>
  );
}

const fmtMonD = (iso) => {
  const full = fmtIsoDate(iso);
  return full ? full.replace(/, \d{4}$/, "") : "";
};

// Calm header (PROJECTS-CALM-PASS 3a): name + one PM Status pill; muted
// phase/area/owner line; next milestone line with the single overdue phrase.
// The weekly overall status is never shown here (it stays in Overall).
export function WeeklyProjectHeader({ project, currentConnections, nav, onOpenRecord }) {
  const status = project.recordStatus ? recordHealthMeta(project) : null;
  // The selected-week payload's locationName is the captured location alias,
  // not a current location lookup. Archives must not borrow current metadata.
  const area = project.areaName || project.area || project.submarket || project.locationAlias || project.locationName || "Area unavailable";
  const location = project.locationName || project.city || null;
  const ms = project.nextMilestone;
  const part = weeklyParticipation(project);
  const deliveryOwner = part.slots.find(slot => slot.key === "delivery_owner")?.people.map(person => person.displayName).filter(Boolean).join(", ");
  const metaBits = [project.phaseLabel || "Phase not set", area].filter(Boolean);
  return (
    <div data-testid="updates-project-header" className="wk-dhead wk-project-head">
      <div data-testid="weekly-title-reference" className="wk-headline">
        <h2 data-testid="weekly-card-name" title={project.name} style={{ margin: 0, minWidth: 0, flex: "0 1 auto", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontSize: T.fsLg, fontWeight: T.fwBold, color: T.dark, lineHeight: 1.2 }}>{project.name}</h2>
        <span title="Project status (PM-owned)" style={{ flexShrink: 0 }}>
          {status ? <StatusPill size="sm" value={status.label} fg={status.color} bg={status.bg} testId={`weekly-overall-${project.id}`} /> : <StatusPill size="sm" value="Status not set" testId={`weekly-overall-${project.id}`} />}
        </span>
        <div className="wk-headcontrols">{nav}</div>
      </div>
      <div className="wk-headmeta" data-testid={`weekly-card-meta-${project.id}`} style={{ fontSize: T.fsXs, color: T.mid }}>
        {metaBits.join(" · ")}
        {" · Delivery owner "}{deliveryOwner || (part.available ? "not assigned" : "unavailable")}
      </div>
      <div data-testid={`weekly-milestone-${project.id}`} title={`Next: ${milestoneText(ms)}`} style={{ fontSize: T.fsXs, color: T.mid, marginTop: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        Next: {ms?.name ? <><b style={{ color: T.dark, fontWeight: T.fwSemibold }}>{ms.name}</b>{ms.dueDate ? ` · ${fmtMonD(ms.dueDate)}` : " · date not set"}{milestoneSignal(ms, project.projectTimeZone).overdue ? " · " : ""}<MilestoneSignal milestone={ms} timeZone={project.projectTimeZone} /></> : ms === null ? "none scheduled" : ms === undefined ? "unavailable" : milestoneText(ms)}
      </div>
      <div className="wk-headbottom">
        <div className="wk-chips">
          {location && !currentConnections?.location?.locationId && <span className="wk-chip" data-testid={`weekly-location-chip-${project.id}`} title={location}>{location}</span>}
          <ProjectConnections connections={currentConnections} variant="compact" />
        </div>
        {onOpenRecord && <button type="button" className="wk-record" data-testid="updates-record-open" aria-haspopup="dialog" aria-label={`Open project record for ${project.name}`} onClick={(e) => onOpenRecord(e.currentTarget)}>Open project <span aria-hidden="true">→</span></button>}
      </div>
    </div>
  );
}

export function WeeklyProjectCard({ project, currentConnections, writable, mediaWritable = writable, liveWeek = writable, weekDate, onMutate, frozen = false, nav = null, userKey = null, onOpenRecord, guardRef, onGuardedNavigate }) {
  const articleRef = useRef(null);
  // Dirty guard for the parent: any open weekly editor field blocks switching.
  // (Record edits are guarded by ProjectRecordDrawer's own guardRef.)
  const docsDraft = useRef({ editing: false, busy: false });
  useEffect(() => {
    if (!guardRef) return undefined;
    // Generic heuristic for weekly editors; the always-present Documents add
    // form is excluded and judged by its real draft/busy state instead.
    guardRef.current = () => docsDraft.current.editing || docsDraft.current.busy || [...(articleRef.current?.querySelectorAll('input:not([type="file"]):not([type="hidden"]), textarea, select, [contenteditable="true"]') || [])]
      .some((el) => !el.closest('[data-testid="document-add-form"]'));
    return () => { guardRef.current = null; };
  }, [guardRef]);
  // Expose header height so the sticky image column clears the sticky header.
  useEffect(() => {
    const el = articleRef.current;
    const head = el?.querySelector("[data-testid='updates-project-header']");
    if (!head || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => el.style.setProperty("--wk-head-h", `${Math.round(head.getBoundingClientRect().height)}px`));
    ro.observe(head);
    return () => ro.disconnect();
  }, []);
  const caps = project.capabilities || {};
  const live = Boolean(writable && !frozen && !project.historical && !project.protectedWeek);
  const part = weeklyParticipation(project);
  const archived = Boolean(frozen || project.historical || project.protectedWeek);
  // Photo proxy serves live bytes, so archived views never render photos.
  const team = useMemo(() => ({ ...weeklyTeam(project, weekDate), photosAllowed: !archived }), [project, weekDate, archived]);
  const canAssign = Boolean(caps.canManageTeam && !archived);
  const blank = sectionEmptiness(project);
  const empty = emptyWeeklyPosting(project, { writable: liveWeek, frozen, weekDate });
  // Live documents only for the current, unfrozen week; archives never gain
  // uncaptured live data. Photos in Updates are read-only.
  const liveDocs = Boolean(mediaWritable && liveWeek && !archived);
  const onDocsDraft = useCallback((st) => { docsDraft.current = st || { editing: false, busy: false }; }, []);
  const docs = useProjectDocuments(project.id, { enabled: liveDocs });
  const [panelPref, setPanelPref] = useUpdatesPanelPreference(userKey);
  const [docSelected, setDocSelected] = useState(null);
  useEffect(() => { setDocSelected(null); }, [project.id]);
  return (
    <TeamContext.Provider value={team}>
    <article ref={articleRef} className="wk-reader" data-testid={`weekly-card-${project.id}`} aria-label={project.name}>
      <WeeklyProjectHeader project={project} currentConnections={currentConnections} nav={nav} onOpenRecord={onOpenRecord} />
      <div className="wk-body">
        <SidePanelHost className="wk-sidehost" gap={12}>
          <div style={{ minWidth: 0 }} data-testid="updates-main">
            <SignalStrip project={project} />
            <OverallHero key={`o-${weekDate}-${project.id}`} project={project} can={live && caps.canSummary} live={liveWeek && !frozen} weekDate={weekDate} onMutate={onMutate} compactEmpty={Boolean(empty)} />
            <ThisWeekSection key={`t-${weekDate}-${project.id}`} project={project} caps={{ thisWeek: live && caps.canThisWeek }} weekDate={weekDate} onMutate={onMutate} emptyBar={blank.thisWeek ? EMPTY_BAR_TEXT.thisWeek : null} />
            <FinancialsSection key={`f-${project.id}`} weekDate={weekDate} project={project} can={live && caps.canFinancials} onMutate={onMutate} frozen={frozen} emptyBar={blank.financials ? EMPTY_SECTION_MESSAGE.financials : null} />
            <DecisionsSection key={`d-${project.id}`} weekDate={weekDate} project={project} can={live && caps.canDecisions} onMutate={onMutate} emptyBar={blank.decisions ? EMPTY_BAR_TEXT.decisions : null} />
            <RisksSection key={`r-${project.id}`} weekDate={weekDate} project={project} can={live && caps.canRisks} onMutate={onMutate} frozen={frozen} emptyBar={blank.risks ? EMPTY_BAR_TEXT.risks : null} />
            <TracksSection project={project} part={part} caps={caps} live={live} weekDate={weekDate} onMutate={onMutate} />
            <PmoSection key={`p-${weekDate}-${project.id}`} project={project} can={live && caps.canPmoNote} weekDate={weekDate} onMutate={onMutate} emptyBar={blank.pmo ? EMPTY_BAR_TEXT.pmo : null} />
          </div>
          <UpdatesSidePanel projectId={project.id} pref={panelPref} onPref={setPanelPref}
            team={<TeamCard project={project} canAssign={canAssign} onGuardedNavigate={onGuardedNavigate} />}
            archived={!liveDocs} capturedMedia={project.media} docs={liveDocs ? docs : null}
            docSelected={docSelected} onDocSelect={setDocSelected} docsReadOnly={!liveDocs} onDocsDraft={onDocsDraft} />
        </SidePanelHost>
      </div>
    </article>
    </TeamContext.Provider>
  );
}

export function canContribute(project) {
  const c = project?.capabilities || {};
  return Boolean(c.canSummary || c.canNote || c.canPostTracks?.length || c.canSetTrackStatus?.length || c.canQuietTracks?.length);
}

export function emptyWeeklyPosting(project, { writable, frozen, weekDate } = {}) {
  if (!writable || frozen || project?.historical || project?.protectedWeek) return null;
  const summary = project.summary || {};
  if (["narrative", "accomplishments", "upcoming", "blockers", "pmoNote"].some(key => weeklyText(summary[key]).trim()) ||
      summary.statusOverall || project.lines?.length || project.statuses?.length || project.completions?.length ||
      project.trackResponses?.length || project.sourceTrackResponses?.length) return null;
  const tracks = assignedTrackCompleteness(project);
  const end = new Date(`${weekDate}T00:00:00Z`);
  end.setUTCDate(end.getUTCDate() + 4);
  const friday = Number.isFinite(end.getTime()) ? end.toISOString().slice(0, 10) : "";
  return { tracks, decisionsDue: Array.isArray(project.decisions) ? project.decisions.filter(d => (!d.status || d.status === "open") && d.neededByDate && d.neededByDate.slice(0, 10) <= friday).length : null };
}
